/**
 * @module router/index
 * @description Vue Router 루트 설정 — 10개 도메인 라우트 모듈을 통합하고
 * 전역 beforeEach 가드(mobileRedirect → staffTeamAuth → auth 순서)와
 * afterEach에서 탭 스토어 동기화를 수행합니다.
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useTabStore } from '@/stores/tabs'
import { useMenuStore } from '@/stores/menu'
import publicRoutes from './modules/public'
import dashboardRoutes from './modules/dashboard'
import consultationRoutes from './modules/consultation'
import repairRoutes from './modules/repair'
import infraRoutes from './modules/infra'
import systemRoutes from './modules/system'
import partnerRoutes from './modules/partner'
import mobileRoutes from './modules/mobile'
import toolsRoutes from './modules/tools'
import settlementRoutes from './modules/settlement'
import reportRoutes from './modules/report'
import agentAdminRoutes from './modules/agent-admin'
import partnershipRoutes from './modules/partnership'
import { mobileRedirectGuard } from './guards/mobileRedirect'
import { staffTeamAuthGuard } from './guards/staffTeamAuth'
import { authGuard } from './guards/auth'
import { roleGuard } from './guards/roleGuard'
import { menuGuard } from './guards/menuGuard'

const routes = [
  ...publicRoutes,
  ...dashboardRoutes,
  ...consultationRoutes,
  ...repairRoutes,
  ...infraRoutes,
  ...systemRoutes,
  ...partnerRoutes,
  ...mobileRoutes,
  ...toolsRoutes,
  ...settlementRoutes,
  ...reportRoutes,
  ...agentAdminRoutes,
  ...partnershipRoutes,
  {
    path: '/payment/result',
    name: '결제 결과 페이지',
    component: () => import(/* webpackChunkName: "paymentResult" */ '../views/repairManagement/PaymentResult.vue'),
    meta: { platform: 'web', menuYn: false, authorization: false }
  },
  {
    path: '/mobile/payment/result',
    name: '모바일 결제 결과 페이지',
    component: () => import(/* webpackChunkName: "paymentResultMobile" */ '../views/repairManagement/PaymentResult.vue'),
    meta: { platform: 'mobile', menuYn: false, authorization: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/error/NotFound.vue'),
    meta: { platform: 'web', menuYn: false, authorization: false }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

/**
 * 전역 라우터 가드 — 모바일 리다이렉트, 스태프팀 인증, 일반 인증, 역할 권한, 메뉴 권한 순서로 검사합니다.
 * 각 가드가 true를 반환하면 이후 가드는 실행하지 않습니다.
 * roleGuard는 인증 통과 이후 `meta.roles` 검사를 수행합니다 (SURINP-218).
 * menuGuard는 `meta.menuYn` 화면을 GET /auth/menus 트리로 검사합니다 — 트리 로드를 기다릴 수 있어 async (SURINP-692).
 */
router.beforeEach(async (to, from, next) => {
  if (mobileRedirectGuard(to, from, next, router)) return
  if (staffTeamAuthGuard(to, from, next)) return
  if (authGuard(to, from, next)) return
  if (roleGuard(to, from, next)) return
  if (await menuGuard(to, from, next)) return
  next()
})

/**
 * 라우트 이동 완료 후 탭 스토어와 동기화합니다.
 * 탭 제목은 메뉴 마스터 이름 — 주소로 바로 들어와도 사이드바 메뉴명과 같다 (SURINP-688).
 */
router.afterEach((to) => {
  const tabStore = useTabStore()
  const menuStore = useMenuStore()
  tabStore.syncWithRoute(to, menuStore.findMenuNameByPath(to.fullPath))
})

export { routes }
export default router
